import type { AnalyticsFilters, PlanFilter, CategoryFilter, CountryFilter, StatusFilter } from '@/mocks/superAdminAnalytics';

interface AnalyticsFiltersProps {
  filters: AnalyticsFilters;
  onChange: (filters: AnalyticsFilters) => void;
  hasActiveFilters: boolean;
  onClear: () => void;
}

const planOptions: { value: PlanFilter; label: string }[] = [
  { value: 'todos', label: 'Todos los planes' },
  { value: 'free', label: 'Free' },
  { value: 'pro', label: 'Pro' },
  { value: 'premium', label: 'Premium' },
];

const categoryOptions: { value: CategoryFilter; label: string }[] = [
  { value: 'todas', label: 'Todas las categorías' },
  { value: 'musical', label: 'Musical' },
  { value: 'noticias', label: 'Noticias' },
  { value: 'deportiva', label: 'Deportiva' },
  { value: 'cultural', label: 'Cultural' },
  { value: 'juvenil', label: 'Juvenil' },
  { value: 'religiosa', label: 'Religiosa' },
  { value: 'comunitaria', label: 'Comunitaria' },
];

const countryOptions: { value: CountryFilter; label: string }[] = [
  { value: 'todos', label: 'Todos los países' },
  { value: 'AR', label: 'Argentina' },
  { value: 'CL', label: 'Chile' },
  { value: 'PE', label: 'Perú' },
  { value: 'CO', label: 'Colombia' },
  { value: 'MX', label: 'México' },
  { value: 'ES', label: 'España' },
  { value: 'US', label: 'EE.UU.' },
  { value: 'EC', label: 'Ecuador' },
  { value: 'UY', label: 'Uruguay' },
  { value: 'BO', label: 'Bolivia' },
  { value: 'PY', label: 'Paraguay' },
];

const statusOptions: { value: StatusFilter; label: string }[] = [
  { value: 'todas', label: 'Todos los estados' },
  { value: 'activa', label: 'Activa' },
  { value: 'inactiva', label: 'Inactiva' },
  { value: 'suspendida', label: 'Suspendida' },
];

export default function AnalyticsFilters({ filters, onChange, hasActiveFilters, onClear }: AnalyticsFiltersProps) {
  const update = (patch: Partial<AnalyticsFilters>) => {
    onChange({ ...filters, ...patch });
  };

  return (
    <div className="bg-background-50 border border-background-200 rounded-2xl p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <i className="ri-filter-3-line text-foreground-500 text-xs"></i>
          <h3 className="text-foreground-800 text-xs font-semibold">Filtros</h3>
          {hasActiveFilters && (
            <span className="w-2 h-2 rounded-full bg-accent-500"></span>
          )}
        </div>
        {hasActiveFilters && (
          <button
            onClick={onClear}
            className="flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-medium text-accent-600 hover:bg-accent-50 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-close-line text-[9px]"></i>
            Limpiar
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-2">
        {/* Date From */}
        <div className="flex flex-col gap-1">
          <label className="text-foreground-400 text-[9px] font-medium">Fecha desde</label>
          <input
            type="date"
            value={filters.dateFrom}
            onChange={(e) => update({ dateFrom: e.target.value })}
            className="w-full px-2.5 py-1.5 rounded-lg border border-background-200 bg-background-50 text-foreground-800 text-[11px] font-medium focus:outline-none focus:border-accent-400 focus:ring-1 focus:ring-accent-400/30 transition-all cursor-pointer"
          />
        </div>

        {/* Date To */}
        <div className="flex flex-col gap-1">
          <label className="text-foreground-400 text-[9px] font-medium">Fecha hasta</label>
          <input
            type="date"
            value={filters.dateTo}
            onChange={(e) => update({ dateTo: e.target.value })}
            className="w-full px-2.5 py-1.5 rounded-lg border border-background-200 bg-background-50 text-foreground-800 text-[11px] font-medium focus:outline-none focus:border-accent-400 focus:ring-1 focus:ring-accent-400/30 transition-all cursor-pointer"
          />
        </div>

        {/* Plan */}
        <div className="flex flex-col gap-1">
          <label className="text-foreground-400 text-[9px] font-medium">Plan</label>
          <select
            value={filters.plan}
            onChange={(e) => update({ plan: e.target.value as PlanFilter })}
            className="w-full px-2.5 py-1.5 rounded-lg border border-background-200 bg-background-50 text-foreground-800 text-[11px] font-medium focus:outline-none focus:border-accent-400 focus:ring-1 focus:ring-accent-400/30 transition-all cursor-pointer appearance-none"
            style={{ backgroundImage: 'none' }}
          >
            {planOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        {/* Category */}
        <div className="flex flex-col gap-1">
          <label className="text-foreground-400 text-[9px] font-medium">Categoría</label>
          <select
            value={filters.category}
            onChange={(e) => update({ category: e.target.value as CategoryFilter })}
            className="w-full px-2.5 py-1.5 rounded-lg border border-background-200 bg-background-50 text-foreground-800 text-[11px] font-medium focus:outline-none focus:border-accent-400 focus:ring-1 focus:ring-accent-400/30 transition-all cursor-pointer appearance-none"
            style={{ backgroundImage: 'none' }}
          >
            {categoryOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        {/* Country */}
        <div className="flex flex-col gap-1">
          <label className="text-foreground-400 text-[9px] font-medium">País</label>
          <select
            value={filters.country}
            onChange={(e) => update({ country: e.target.value as CountryFilter })}
            className="w-full px-2.5 py-1.5 rounded-lg border border-background-200 bg-background-50 text-foreground-800 text-[11px] font-medium focus:outline-none focus:border-accent-400 focus:ring-1 focus:ring-accent-400/30 transition-all cursor-pointer appearance-none"
            style={{ backgroundImage: 'none' }}
          >
            {countryOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        {/* Status */}
        <div className="flex flex-col gap-1">
          <label className="text-foreground-400 text-[9px] font-medium">Estado radio</label>
          <select
            value={filters.status}
            onChange={(e) => update({ status: e.target.value as StatusFilter })}
            className="w-full px-2.5 py-1.5 rounded-lg border border-background-200 bg-background-50 text-foreground-800 text-[11px] font-medium focus:outline-none focus:border-accent-400 focus:ring-1 focus:ring-accent-400/30 transition-all cursor-pointer appearance-none"
            style={{ backgroundImage: 'none' }}
          >
            {statusOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}